import { Suspense, lazy, useEffect } from "react";
import { BrowserRouter, Route, Routes } from "react-router-dom";
import { Spinner } from "../components/Spinner";
import { ScrollToTop } from "../helpers/scrollToTop";
import { useAppSelector } from "../redux/hooks";
import { axiosInstance } from "../services/axiosInstance";
import { PrivateRoutes } from "./PrivateRoutes";
import { PublicRoutes } from "./PublicRoutes";

// Lazy load pages
const HomePage = lazy(() =>
  import("../pages/HomePage").then(module => ({ default: module.HomePage }))
);
const LoginPage = lazy(() =>
  import("../pages/LoginPage").then(module => ({ default: module.LoginPage }))
);
const RegisterPage = lazy(() =>
  import("../pages/RegisterPage").then(module => ({
    default: module.RegisterPage,
  }))
);
const ForgotPasswordPage = lazy(() =>
  import("../pages/ForgotPasswordPage").then(module => ({
    default: module.ForgotPasswordPage,
  }))
);
const ResetPasswordPage = lazy(() =>
  import("../pages/ResetPasswordPage").then(module => ({
    default: module.ResetPasswordPage,
  }))
);
const SuccessRegisterPage = lazy(() =>
  import("../pages/SuccessRegisterPage").then(module => ({
    default: module.SuccessRegisterPage,
  }))
);
const ReservaTourPage = lazy(() =>
  import("../pages/ReservaTourPage").then(module => ({
    default: module.ReservaTourPage,
  }))
);
const ReservaExitosaPage = lazy(() =>
  import("../pages/ReservaExitosaPage").then(module => ({
    default: module.ReservaExitosaPage,
  }))
);
const ProfilePage = lazy(() =>
  import("../pages/ProfilePage").then(module => ({
    default: module.ProfilePage,
  }))
);
const ReserveConfirmPage = lazy(() =>
  import("../pages/ReserveConfirmPage").then(module => ({
    default: module.ReserveConfirmPage,
  }))
);
const MyTourPages = lazy(() =>
  import("../pages/MyToursPage").then(module => ({
    default: module.MyTourPages,
  }))
);
const ComingSoonPage = lazy(() =>
  import("../pages/ComingSoonPage").then(module => ({
    default: module.ComingSoonPage,
  }))
);
const ContactoPage = lazy(() =>
  import("../pages/ContactoPage").then(module => ({
    default: module.ContactoPage,
  }))
);
const MaterialesPage = lazy(() =>
  import("../pages/MaterialesPage").then(module => ({
    default: module.MaterialesPage,
  }))
);
const QuienesSomosPage = lazy(() =>
  import("../pages/QuienesSomosPage").then(module => ({
    default: module.QuienesSomosPage,
  }))
);
const PlaygroundPage = lazy(() =>
  import("../pages/PlaygroundPage").then(module => ({
    default: module.PlaygroundPage,
  }))
);
const TermsPage = lazy(() =>
  import("../pages/TermsPage").then(module => ({ default: module.TermsPage }))
);
const EnterTourPage = lazy(() =>
  import("../pages/EnterTourPage").then(module => ({
    default: module.EnterTourPage,
  }))
);
const EncuestaPage = lazy(() =>
  import("../pages/EncuestaPage").then(module => ({
    default: module.EncuestaPage,
  }))
);
const SalaDeEspera = lazy(() =>
  import("../pages/SalaDeEspera").then(module => ({
    default: module.SalaDeEspera,
  }))
);
const ConfirmTourPage = lazy(() =>
  import("../pages/ConfirmTour").then(module => ({
    default: module.ConfirmTourPage,
  }))
);
const CancelTourPage = lazy(() =>
  import("../pages/CancelTour").then(module => ({
    default: module.CancelTourPage,
  }))
);
const UnsuscribeAlertsPage = lazy(() =>
  import("../pages/UnsuscribeAlertsPage").then(module => ({
    default: module.UnsuscribeAlertsPage,
  }))
);

const LoadingFallback = () => (
  <div
    style={{
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      height: "100vh",
      width: "100vw",
    }}
  >
    <Spinner width="60px" height="60px" color="#3a89ca" />
  </div>
);

export const AppRouter = () => {
  const token = useAppSelector(state => state.auth.token);

  useEffect(() => {
    if (token) {
      axiosInstance.defaults.headers.common["Authorization"] =
        `Bearer ${token}`;
    } else {
      delete axiosInstance.defaults.headers.common["Authorization"];
    }
  }, [token]);

  return (
    <>
      <BrowserRouter>
        <ScrollToTop />
        <Suspense fallback={<LoadingFallback />}>
          <Routes>
            <Route element={<PublicRoutes />}>
              <Route path="/auth/login" element={<LoginPage />} />
              <Route path="/auth/register" element={<RegisterPage />} />
              <Route
                path="/auth/forgot-password"
                element={<ForgotPasswordPage />}
              />
              <Route
                path="/api/reset-password/:token/:email"
                element={<ResetPasswordPage />}
              />
              <Route
                path="/api/email/verifyAccount/:token"
                element={<SuccessRegisterPage />}
              />
            </Route>
            <Route element={<PrivateRoutes />}>
              <Route path="/reserva-tour" element={<ReservaTourPage />} /> */
              <Route
                path="/reserva/reserva-exitosa"
                element={<ReservaExitosaPage />}
              />
              <Route path="/user/profile" element={<ProfilePage />} />
              <Route path="/confirm-reserve" element={<ReserveConfirmPage />} />
              <Route path="/mis-tours" element={<MyTourPages />} />
            </Route>
            <Route path="/comingsoon" element={<ComingSoonPage />} />
            {/* <Route path="/recorridorapido" element={<PreRecorridoRapidoPage />} /> */}
            <Route path="/recorridorapido" element={<h1>Access Denied</h1>} />
            <Route path="/contacto" element={<ContactoPage />} />
            <Route path="/materiales" element={<MaterialesPage />} />
            <Route path="/quienes-somos" element={<QuienesSomosPage />} />
            <Route path="/juegos" element={<PlaygroundPage />} />
            <Route path="/terminos-condiciones" element={<TermsPage />} />
            <Route path="/ingresar-tour" element={<EnterTourPage />} />
            <Route path="/encuesta/:id" element={<EncuestaPage />} />
            <Route path="/sala-de-espera" element={<SalaDeEspera />} />
            <Route path="/confirm-tour" element={<ConfirmTourPage />} />
            <Route path="/cancel-tour" element={<CancelTourPage />} />
            <Route
              path="/unsuscribe-alerts"
              element={<UnsuscribeAlertsPage />}
            />
            <Route path="/" element={<HomePage />} />
          </Routes>
        </Suspense>
      </BrowserRouter>
    </>
  );
};
